/**
 * Module dependencies,
 */

import { AppointmentFormData, postAppointment } from 'src/requests/appointment';
import { BookAppointmentForm } from 'src/components/forms/book-appointment';
import { Breadcrumbs } from 'src/components/core/breadcrumbs';
import { DetailedService } from 'src/types/service';
import { FieldsGrid, Title } from 'src/components/forms/shared/styles';
import { GetServerSideProps, NextPage } from 'next';
import { Locale } from 'i18n-routes';
import { RawHtml } from 'src/components/core/raw-html';
import { SEO } from 'src/components/core/seo';
import { SSGProps } from 'src/types/app';
import { Text } from 'src/components/core/text';
import { TriggerNavbarTheme } from 'src/components/layout/navbar/trigger-navbar-theme';
import { getLocalizedCategoryByServiceSlug } from 'src/core/utils/routes';
import { getService } from 'src/requests/service';
import { media } from 'src/styles/utils/media';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { toast } from 'src/providers/toast';
import { useCallback, useMemo } from 'react';
import { useRouteResolver } from 'src/hooks/use-route-resolver';
import { useRouter } from 'next/router';
import { useRouterHistory } from 'src/providers/route-history';
import { useTranslation } from 'next-i18next';
import camelCase from 'lodash/camelCase';
import styled from 'styled-components';

/**
 * `Props` type.
 */

type Props = {
  data: {
    service: DetailedService;
  };
};

/**
 * `StyledTitle` styled component.
 */

const StyledTitle = styled(Title)`
  ${media.max.ms`
    margin-bottom: 0;
  `}
`;

/**
 * `Description` styled component.
 */

const Description = styled(Text)`
  ${media.max.ms`
    margin-bottom: 3rem;
  `}
`;

/**
 * `BookServiceAppointment` page.
 */

const BookServiceAppointment: NextPage<Props> = props => {
  const { category, description, id, subCategory, title } = props.data.service;
  const { locale } = useRouter();
  const { previousRoute } = useRouterHistory();
  const { t } = useTranslation(['common', 'appointment']);
  const resolveRoute = useRouteResolver();
  const breadcrumbs = useMemo(
    () => [
      {
        label: t('common:terms.services'),
        url: resolveRoute('allServices')
      },
      {
        label: t(`common:service.category.${camelCase(category)}`),
        url: resolveRoute('serviceType', { slug: category })
      },
      {
        label: t(`common:service.subCategory.${camelCase(subCategory)}`),
        url: resolveRoute('services', {
          subCategory: getLocalizedCategoryByServiceSlug(
            locale as Locale,
            subCategory
          )
        })
      },
      {
        label: title
      }
    ],
    [category, locale, resolveRoute, subCategory, t, title]
  );

  const onSubmit = useCallback(
    async (formData: AppointmentFormData, reset: () => void) => {
      try {
        const response = await postAppointment({
          ...formData,
          serviceCategory: category,
          serviceId: id,
          serviceSubCategory: subCategory
        });

        toast.success(t('appointment:network.success'));

        reset();

        return response;
      } catch (error) {
        toast.error(t('appointment:network.error'));

        return error;
      }
    },
    [category, id, subCategory, t]
  );

  return (
    <>
      <SEO title={t('title')} />

      <TriggerNavbarTheme theme={'light'} />

      <BookAppointmentForm
        modalCloseButtonLink={previousRoute}
        onSubmit={onSubmit}
      >
        <FieldsGrid>
          <div>
            <Breadcrumbs items={breadcrumbs} />

            <StyledTitle>{title}</StyledTitle>
          </div>

          <RawHtml element={Description}>{description}</RawHtml>
        </FieldsGrid>
      </BookAppointmentForm>
    </>
  );
};

/**
 * Export `getServerSideProps`.
 */

export const getServerSideProps: GetServerSideProps<
  SSGProps
> = async context => {
  try {
    const { locale, params } = context;
    const service = await getService(params?.slug as string, locale as Locale);

    if (!service) {
      throw new Error('notFound');
    }

    return {
      props: {
        data: { service },
        hideFooter: true,
        hideNavigation: true,
        navbarTheme: 'light',
        ...(await serverSideTranslations(locale as string, [
          'common',
          'appointment'
        ]))
      }
    };
  } catch (error) {
    return {
      notFound: true
    };
  }
};

/**
 * Export `BookServiceAppointment` component.
 */

export default BookServiceAppointment;
